import AxiosInstance from "../config/AxiosInstance";
import AxiosInstanceFormData from "../config/AxiosInstanceFormData";

export interface FloorPlanDetail {
    id: number;
    name: string;
    type: string;
    x: number;
    y: number;
    width: number;
    height: number;
    radius: number;
    rotation: number;
    position: number;
    maxCovers: number;
    minCovers: number;
    isReserved: boolean;
    reservedFrom: Date | null;
    reservedTo: Date | null;
    shape: "circle" | "oval" | "rectangle" | "square";
}

export interface FloorPlanDetailPayload {
    name: string;
    type: string;
    shape: string;
    x: number;
    y: number;
    width: number;
    height: number;
    radius: number;
    rotation: number;
    position: number;
    maxCovers: number;
    minCovers: number;
    isReserved: boolean;
    reservedFrom: Date | null;
    reservedTo: Date | null;
}

export interface SalePointFloorPlan {
    salePointId: string;
    position: number;
}

export interface FloorPlanPayload {
    locationId: string;
    description: string;
    planName: string;
    position: number;
    image: string;
    // FIN-1115: optional, server defaults to false when omitted (FIN-1112).
    // null is allowed because the underlying column is nullable.
    coversEnabled?: boolean | null;
    floorPlanDetails: FloorPlanDetailPayload[];
    salePointFloorPlans: SalePointFloorPlan[];
}


export const uploadFile = async (payload: FormData) => {
    try {
        const { data } = await AxiosInstance.post('/api/files/upload', payload, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return { data };
    } catch (error) {
        throw error;
    }
}
export const createFloorPlan = async (payload: FormData) => {
    try {
        const { data } = await AxiosInstanceFormData.post('/api/inventory/floor-plans', payload);
        return { data };
    } catch (error) {
        throw error;
    }
}

export const updateFloorPlan = async (id: string, payload: FormData) => {
    try {
        const { data } = await AxiosInstanceFormData.put('/api/inventory/floor-plans', payload, {
            params: { id: id }
        });
        return { data };
    } catch (error) {
        throw error;
    }
};

// Flips only is_active; the PUT above re-creates every table from its payload.
export const updateFloorPlanStatus = async (id: string, isActive: boolean) => {
    try {
        const { data } = await AxiosInstance.patch(`/api/inventory/floor-plans/${id}/status`, { isActive });
        return { data };
    } catch (error) {
        throw error;
    }
};

export const getDetailFloorPlan = async (id: string, locationId: string) => {
    try {
        const { data } = await AxiosInstanceFormData.get('/api/inventory/floor-plans', {
            params: { id: id, locationId: locationId }
        });
        return { data };
    } catch (error) {
        throw error;
    }
};


export const getListFloorPlan = async (page: any, limit: any, searchValue: any, locationId: any,) => {
    try {
        const { data } = await AxiosInstance.get('/api/inventory/floor-plans/list', {
            params: {
                page: page,
                limit: limit,
                searchValue: searchValue,
                locationId: locationId,
            }
        });
        return { data };
    } catch (error) {
        throw error;
    }
};
export const deleteFloorPlan = async (id: string) => {
    try {
        const { data } = await AxiosInstance.delete('/api/inventory/floor-plans', {
            params: { id: id }
        });
        return { data };
    } catch (error) {
        throw error;
    }
};

/**
 * One `dbo.floor_plan_details` row as `/floor-plans-details/list` returns it —
 * the shape the reservations table picker needs (name + covers), not the
 * canvas geometry `FloorPlanDetail` above carries.
 */
export interface FloorPlanTable {
    id: string;
    floorPlanId: string;
    name: string;
    /** `floor_plan_type_enum`; only "table" rows are seatable. */
    type: string;
    shape: string;
    minCovers: number | null;
    maxCovers: number | null;
    isReserved: boolean;
}

/**
 * Tables of a location's floor plan, for the reservations table picker.
 *
 * `searchValue` MUST be sent as "" rather than omitted: the back end treats
 * `searchValue === ''` as "no filter" and otherwise builds an
 * `ILIKE %<value>%` clause — an undefined value would search for the literal
 * string "undefined". The endpoint 404s when the location has no floor plan,
 * which callers surface as an empty picker.
 */
export const getFloorPlanTables = async (locationId: string, limit = 200, page = 1) => {
    try {
        const { data } = await AxiosInstance.get('/api/inventory/floor-plans-details/list', {
            params: {
                page: page,
                limit: limit,
                searchValue: '',
                locationId: locationId,
            }
        });
        return { data };
    } catch (error) {
        throw error;
    }
};
